import { useEffect, useRef, useState } from "react";
import { Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SYDNEY = { lat: -33.8688, lng: 151.2093 };

export const Circle = ({ center, radius }) => {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (!map || !window.google) { return undefined; }
    ref.current = new window.google.maps.Circle({ map, center, radius, strokeColor: "#00A85C", strokeWeight: 2, fillColor: "#00A85C", fillOpacity: 0.15, clickable: false });
    return () => ref.current.setMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  useEffect(() => {
    if (ref.current) { ref.current.setCenter(center); ref.current.setRadius(radius); }
  }, [center, radius]);
  return null;
};

const Recenter = ({ pos }) => {
  const map = useMap();
  const lat = pos?.lat;
  const lng = pos?.lng;
  useEffect(() => {
    if (map && lat != null) { map.panTo({ lat, lng }); if (map.getZoom() < 15) map.setZoom(16); }
  }, [map, lat, lng]);
  return null;
};

const AddressSearch = ({ onPick, initial }) => {
  const lib = useMapsLibrary("geocoding");
  const [q, setQ] = useState(initial || "");
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState("");
  const run = async () => {
    if (!lib || !q.trim()) return;
    try {
      const { results: r } = await new lib.Geocoder().geocode({ address: q, componentRestrictions: { country: "AU" } });
      setResults(r.slice(0, 5));
      setMsg(r.length ? "" : "No matching addresses found.");
    } catch {
      setResults([]);
      setMsg("Address search is unavailable. Check the Geocoding API is enabled for this Google key — you can still click the map to place a pin.");
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }} placeholder="Search an Australian address" className="h-11 rounded-xl" data-testid="map-address-search-input" />
        <Button type="button" onClick={run} variant="outline" className="h-11 rounded-xl" data-testid="map-address-search-btn"><Search className="h-4 w-4" /></Button>
      </div>
      {msg && <p className="text-xs text-amber-700" data-testid="map-search-message">{msg}</p>}
      {results.length > 0 && (
        <ul className="overflow-hidden rounded-xl border border-slate-200" data-testid="map-search-results">
          {results.map((r) => (
            <li key={r.place_id}>
              <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`map-search-result-${r.place_id}`}
                onClick={() => { onPick({ lat: r.geometry.location.lat(), lng: r.geometry.location.lng(), address: r.formatted_address, place_id: r.place_id }); setResults([]); setQ(r.formatted_address); }}>
                {r.formatted_address}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const WorkplaceMap = ({ value, radius, onChange, height = 300, searchable = true }) => {
  if (!MAPS_KEY) return <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">Google Maps key is not configured.</div>;
  const pos = value?.lat != null ? { lat: value.lat, lng: value.lng } : null;
  return (
    <div className="space-y-3" data-testid="workplace-map">
      {searchable && <AddressSearch onPick={onChange} initial={value?.address} />}
      <div className="overflow-hidden rounded-2xl border border-slate-200" style={{ height }}>
        <Map defaultCenter={pos || SYDNEY} defaultZoom={pos ? 16 : 11} gestureHandling="greedy" disableDefaultUI zoomControl
          onClick={(e) => { const ll = e.detail.latLng; if (ll && onChange) onChange({ lat: ll.lat, lng: ll.lng }); }}>
          {pos && <Marker position={pos} draggable={!!onChange} onDragEnd={(e) => onChange && onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })} />}
          {pos && <Circle center={pos} radius={radius || 150} />}
          <Recenter pos={pos} />
        </Map>
      </div>
      {onChange && <p className="text-xs text-slate-500">Search, click the map or drag the pin to set the exact spot.</p>}
    </div>
  );
};
